// The server's "now" for the banner (countdown, warm-up pill, go live), not the browser clock:
// fullstack /api/v1/clock gives the time once, and it advances with performance.now() (monotonic,
// unaffected by the browser's clock). Synced on load, when the user signs in or out, every few
// minutes, and on request (refreshServerClock: the /test page after changing its override).
// The server applies a tester's override for their own session only (fullstack
// server/services/clock/serverClock.js). Until the first answer, isSynced is false; if the
// server can't be reached, the browser clock stands in.

import {ref, watch} from "vue";
import {DateTime} from "luxon";
import {authFetch, readJson} from "@/composables/useAuthFetch";
import useUserSafe from "@/composables/useUserSafe";

const RESYNC_MS = 5 * 60 * 1000;
const anchor = ref(null); // {serverMs, perfMs}: the server's now at a performance.now() reading
const offsetMs = ref(0);
const isOverridden = ref(false);
const isSynced = ref(false);
let hasStarted = false;

function setAnchor(serverMs, perfMs) {
  anchor.value = {serverMs, perfMs};
  isSynced.value = true;
}

// the server answered at some point during the request: assume halfway
async function fetchServerClock() {
  const sentAt = performance.now();
  const response = await authFetch("/api/v1/clock", {cache: "no-store"});
  const receivedAt = performance.now();
  const clock = response.ok ? await readJson(response, null) : null;
  if (!clock) return setAnchor(Date.now(), receivedAt);
  offsetMs.value = clock.offsetMs || 0;
  isOverridden.value = !!clock.isOverridden;
  setAnchor(clock.now + (receivedAt - sentAt) / 2, receivedAt);
}

export function getServerNow() {
  if (!anchor.value) return DateTime.now();
  const {serverMs, perfMs} = anchor.value;
  return DateTime.fromMillis(serverMs + performance.now() - perfMs);
}

export const refreshServerClock = fetchServerClock;

// back to real time: this session's test override cleared (the /test page, the test clock
// button: TestClockOff.vue), then synced again
export async function clearClockOverride() {
  const headers = {"Content-Type": "application/json"};
  const body = JSON.stringify({target: null});
  await authFetch("/api/v1/user/clock-override", {method: "POST", headers, body});
  await fetchServerClock();
}

function startSyncing() {
  hasStarted = true;
  const {isSignedIn, isLoaded} = useUserSafe();
  watch([isLoaded, isSignedIn], ([loaded]) => loaded && fetchServerClock(), {immediate: true});
  setInterval(fetchServerClock, RESYNC_MS);
}

export default function useServerClock() {
  if (!hasStarted) startSyncing();
  return {getServerNow, isSynced, isOverridden, offsetMs, refreshServerClock};
}
